export const DEFAULT_INGEST_SOURCE = 'google_drive';

export const INGEST_SOURCE_OPTIONS = [
  {
    value: 'google_drive',
    label: 'Google Drive',
    description: 'Importe uma pasta inteira da sua conta Google.',
    recommended: true,
  },
  {
    value: 'url',
    label: 'Link direto',
    description: 'Um PDF, DOC ou DOCX por URL pública.',
  },
  {
    value: 's3',
    label: 'Amazon S3',
    description: 'Arquivos em um bucket ou prefixo S3.',
  },
  {
    value: 'contentful',
    label: 'Contentful',
    description: 'Assets e entries de um space Contentful.',
  },
];

export const KB_STATUS_LABELS = {
  pending: 'Aguardando processamento',
  ingesting: 'Processando conteúdo',
  syncing: 'Indexando',
  active: 'Ativo',
  error: 'Erro',
};

export const KB_STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  ingesting: 'bg-sky-50 text-sky-700 border-sky-200',
  syncing: 'bg-violet-50 text-violet-700 border-violet-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  error: 'bg-red-50 text-red-700 border-red-200',
};

export const SOURCE_TYPE_LABELS = {
  url: 'Link',
  s3: 'S3',
  google_drive: 'Google Drive',
  contentful: 'Contentful',
  file_to_text: 'Documento',
  manual: 'Manual',
};

export const KB_USAGE_TYPE_OPTIONS = [
  {
    value: 'context',
    label: 'Contexto',
    description: 'Usada só para enriquecer o prompt da IA.',
  },
  {
    value: 'evidence',
    label: 'Evidência',
    description: 'Enriquece o prompt e pode enviar o arquivo original (botão).',
  },
];

export const KB_USAGE_TYPE_LABELS = {
  context: 'Contexto',
  evidence: 'Evidência',
};

export const KB_USAGE_TYPE_STYLES = {
  context: 'bg-slate-100 text-slate-700 border-slate-200',
  evidence: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

export const IMPORT_FILE_STATUS_LABELS = {
  pending: 'Pendente',
  processing: 'Processando',
  completed: 'Concluído',
  failed: 'Falhou',
  skipped: 'Ignorado',
  removed: 'Removido da fonte',
};

export const IMPORT_FILE_STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  processing: 'bg-sky-50 text-sky-700 border-sky-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  skipped: 'bg-slate-100 text-slate-600 border-slate-200',
  removed: 'bg-amber-50 text-amber-800 border-amber-200',
};

export function canSyncKnowledgeBase(kb) {
  return !['pending', 'ingesting', 'syncing'].includes(kb.status);
}

export function slugFromName(name) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

const GOOGLE_DRIVE_FOLDER_URL_PATTERN =
  /drive\.google\.com\/(?:drive\/)?(?:u\/\d+\/)?folders\/([a-zA-Z0-9_-]+)/;

export function parseGoogleDriveFolderId(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }
  const match = url.trim().match(GOOGLE_DRIVE_FOLDER_URL_PATTERN);
  return match ? match[1] : null;
}

export function googleDriveFolderUrl(folderId) {
  if (!folderId) {
    return '';
  }
  return `https://drive.google.com/drive/folders/${folderId}`;
}

export function buildIngestPayload(source, values, options = {}) {
  const base = {
    name: values.name.trim(),
    source,
  };

  if (source === 'url') {
    return {
      ...base,
      url: values.url.trim(),
    };
  }

  if (source === 's3') {
    return {
      ...base,
      s3_url: values.s3Url.trim(),
      aws_access_key_id: values.awsAccessKeyId.trim(),
      aws_secret_access_key: values.awsSecretAccessKey.trim(),
    };
  }

  if (source === 'contentful') {
    const payload = {
      ...base,
      space_id: values.spaceId.trim(),
      access_token: values.accessToken.trim(),
      api_url: values.apiUrl.trim() || 'https://cdn.contentful.com',
      environment: values.environment.trim() || 'master',
      locale: values.locale.trim() || '*',
      include_assets: values.includeAssets !== false,
      include_entries: values.includeEntries !== false,
    };

    const contentTypes = values.contentTypes
      ?.split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    if (contentTypes?.length) {
      payload.content_types = contentTypes;
    }

    return payload;
  }

  const payload = {
    ...base,
    folder_url: values.folderUrl.trim(),
  };

  const folderId = values.folderId?.trim() || parseGoogleDriveFolderId(values.folderUrl);
  if (folderId) {
    payload.folder_id = folderId;
  }

  if (options.authMode === 'domain_delegation') {
    payload.auth_mode = 'domain_delegation';
    payload.workspace_email = values.workspaceEmail.trim();
  } else {
    payload.auth_mode = 'oauth';
  }

  return payload;
}
